// Tagesziel: Produktions-Hedef je Abteilung
// Kapazität = (Engpass × Std/Werker × Effizienz) ÷ 5 Arbeitstage
import { useEffect, useState, useCallback } from 'react';
import { Target } from 'lucide-react';
import { api } from '../lib/api';

type Row = {
  abteilung_id: number; abteilung_name: string; abteilung_farbe: string;
  engpass_anzahl: number | null; engpass_typ: string | null;
  tageskapazitaet: number; ist_stunden: number; ist_count: number;
  auslastung_percent: number | null;
};

function barColor(pct: number | null): string {
  if (pct === null) return 'bg-slate-200';
  if (pct >= 100) return 'bg-emerald-500';
  if (pct >= 80)  return 'bg-amber-500';
  if (pct >= 40)  return 'bg-orange-400';
  return 'bg-red-400';
}

function textColor(pct: number | null): string {
  if (pct === null) return 'text-slate-500';
  if (pct >= 100) return 'text-emerald-700';
  if (pct >= 80)  return 'text-amber-700';
  if (pct >= 40)  return 'text-orange-700';
  return 'text-red-700';
}

export default function TageszielWidget() {
  const [data, setData] = useState<Row[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.tagesziel.get();
      setData(r.data);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Fehler');
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2">
          <Target className="w-4 h-4 text-blue-600" />
          Tagesziel — heutige Produktion pro Abteilung
        </h2>
        <div className="text-xs text-slate-500">
          {new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' })}
        </div>
      </div>

      {err && <div className="text-sm text-red-600">{err}</div>}
      {!data && !err && (
        <div className="space-y-2">
          {[1,2,3,4].map(i => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}
        </div>
      )}

      {data && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase border-b border-slate-200">
              <tr>
                <th className="text-left px-2 py-2 font-medium">Abteilung</th>
                <th className="text-left px-2 py-2 font-medium">Engpass</th>
                <th className="text-right px-2 py-2 font-medium">Kapazität/Tag</th>
                <th className="text-right px-2 py-2 font-medium">Ist heute</th>
                <th className="text-right px-2 py-2 font-medium w-2/5">Auslastung</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map(r => (
                <tr key={r.abteilung_id} className="hover:bg-slate-50">
                  <td className="px-2 py-2 font-medium" style={{ borderLeft: `4px solid ${r.abteilung_farbe}` }}>
                    {r.abteilung_name}
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-500">
                    {r.engpass_anzahl ? `${r.engpass_anzahl}× ${r.engpass_typ}` : '—'}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums font-medium">
                    {r.tageskapazitaet}h
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    <span className={textColor(r.auslastung_percent)}>
                      {r.ist_stunden}h
                    </span>
                    {r.ist_count > 0 && (
                      <span className="ml-1 text-xs text-slate-400">({r.ist_count})</span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${barColor(r.auslastung_percent)} transition-all`}
                          style={{ width: `${Math.min(100, r.auslastung_percent ?? 0)}%` }}
                        />
                      </div>
                      <span className={`text-xs font-semibold tabular-nums w-12 text-right ${textColor(r.auslastung_percent)}`}>
                        {r.auslastung_percent !== null ? `${r.auslastung_percent}%` : '—'}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 text-[11px] text-slate-400">
        Kapazität/Tag = (Engpass-Anzahl × 40h × Effizienz 0.85) ÷ 5 Arbeitstage ·
        Ist heute = SUM(zeit_pro_stueck × stueck) der heute fertig markierten Aufträge
      </div>
    </div>
  );
}
